import { useCallback, useEffect, useRef, useState } from 'react'
import {
  ASSISTANT_CHAT_CONTAINER_ID,
  DEFAULT_ASSISTANT_ID,
  DEFAULT_IS_AI_TEXT_NORMAL,
  EMBED_SCRIPT_ID,
  getChatEmbedHostOptions,
  getChatEmbedOrigin,
  getEmbedScriptUrl,
} from './assistantChatConfig'

/**
 * 載入 embed.js 並以 fixed-bottom-right + minimizable 初始化。
 * icon / iframe 開關由 embed.js 負責，React 不自建 launcher。
 * @param {{ assistantId?: string, containerId?: string, isAITextNormal?: boolean }} [options]
 */
export function useAssistantChatEmbed(options = {}) {
  const assistantId = options.assistantId ?? DEFAULT_ASSISTANT_ID
  const containerId = options.containerId ?? ASSISTANT_CHAT_CONTAINER_ID
  const isAITextNormal = options.isAITextNormal ?? DEFAULT_IS_AI_TEXT_NORMAL

  const [scriptReady, setScriptReady] = useState(
    typeof window !== 'undefined' && Boolean(window.AssistantChat)
  )
  const initDoneRef = useRef(false)

  const getChatIframe = useCallback(() => {
    return document.querySelector(`#${containerId} iframe`)
  }, [containerId])

  const postToChatEmbed = useCallback(
    (payload) => {
      const iframe = getChatIframe()
      const targetOrigin = getChatEmbedOrigin()
      if (iframe?.contentWindow) {
        iframe.contentWindow.postMessage(payload, targetOrigin)
      }
    },
    [getChatIframe]
  )

  const postEmbedUiOptions = useCallback(() => {
    postToChatEmbed({
      source: 'chatbot-parent',
      isAITextNormal,
      isFullscreen: false,
    })
  }, [postToChatEmbed, isAITextNormal])

  const bindIframeLoadOnce = useCallback(() => {
    const iframe = getChatIframe()
    if (!iframe) return
    iframe.addEventListener('load', () => postEmbedUiOptions(), { once: true })
  }, [getChatIframe, postEmbedUiOptions])

  const triggerStartMic = useCallback(() => {
    postToChatEmbed({
      source: 'chatbot-parent',
      type: 'START_MIC',
      isFullscreen: false,
    })
  }, [postToChatEmbed])

  const tryInitAssistant = useCallback(() => {
    if (typeof window === 'undefined' || !window.AssistantChat) return
    if (initDoneRef.current) return
    if (window.AssistantChat.isInitialized(containerId)) {
      initDoneRef.current = true
      return
    }
    if (!document.getElementById(containerId)) return

    try {
      window.AssistantChat.init(assistantId, containerId, {
        position: 'fixed-bottom-right',
        minimizable: true,
        width: 400,
        height: 600,
        ...getChatEmbedHostOptions(),
      })
      initDoneRef.current = true
      bindIframeLoadOnce()
    } catch (e) {
      console.error('AssistantChat init error:', e)
    }
  }, [assistantId, bindIframeLoadOnce, containerId])

  useEffect(() => {
    if (typeof window === 'undefined') return

    if (window.AssistantChat) {
      setScriptReady(true)
      return
    }

    const existing = document.getElementById(EMBED_SCRIPT_ID)
    if (existing) {
      if (window.AssistantChat) setScriptReady(true)
      else {
        existing.addEventListener('load', () => setScriptReady(true), {
          once: true,
        })
      }
      return
    }

    const script = document.createElement('script')
    script.id = EMBED_SCRIPT_ID
    script.src = getEmbedScriptUrl()
    script.async = true
    script.onload = () => setScriptReady(true)
    script.onerror = () => {
      console.error('Failed to load embed.js')
    }
    document.body.appendChild(script)
  }, [])

  useEffect(() => {
    if (!scriptReady) return
    tryInitAssistant()
  }, [scriptReady, tryInitAssistant])

  useEffect(() => {
    const embedOrigin = getChatEmbedOrigin()

    function onEmbedMessage(event) {
      if (event.origin !== embedOrigin) return
      const d = event.data
      if (d?.source !== 'chatbot-embed') return

      if (d.type === 'READY') {
        postEmbedUiOptions()
      }
    }

    function onKeyDown(event) {
      if (event.key !== 'F9') return
      event.preventDefault()
      triggerStartMic()
    }

    window.addEventListener('message', onEmbedMessage)
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('message', onEmbedMessage)
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [postEmbedUiOptions, triggerStartMic])

  useEffect(() => {
    return () => {
      if (typeof window === 'undefined' || !window.AssistantChat) return
      try {
        window.AssistantChat.destroy(containerId)
      } catch (e) {
        /* ignore */
      }
      initDoneRef.current = false
    }
  }, [containerId])

  return {
    containerId,
    postToChatEmbed,
    postEmbedUiOptions,
    triggerStartMic,
    isAITextNormal,
  }
}
